import { useState, useEffect, useRef, useCallback } from 'react';
import { fulcrumListUnspentTokens, type FulcrumUtxo } from '@/lib/fulcrum';
import { toBaseUnits, type Stablecoin } from '@/lib/stablecoin';

export type TokenPaymentStatus = 'idle' | 'waiting' | 'detected' | 'error';

interface DetectedTokenPayment {
  txid: string;
  /** On-chain base units received (BigInt → string for easy passing). */
  amountUnits: string;
}

export interface TokenDetectionDebug {
  polls: number;
  lastTokenUtxos: number; // token UTXOs seen on last poll
  lastPolledAt: number;
  lastError: string;
  server: string; // which Fulcrum server answered
  address: string; // token-aware address being watched
  expectedUnits: string; // base units we're waiting for
}

const POLL_INTERVAL_MS = 5_000;

/**
 * Watch an address for an incoming CashToken payment of a specific stablecoin.
 *
 * Matches any NEW token UTXO whose `token_data.category` equals the coin's
 * category AND whose fungible amount is ≥ 80% of the expected amount (same
 * tolerance as the BCH flow, to absorb rounding / partial-dust quirks).
 */
export function useTokenPaymentDetection(
  address: string,
  coin: Stablecoin,
  expectedDisplayAmount: number,
  active: boolean,
  onDetected: (payment: DetectedTokenPayment) => void
) {
  const [status, setStatus] = useState<TokenPaymentStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [debug, setDebug] = useState<TokenDetectionDebug>({
    polls: 0, lastTokenUtxos: 0, lastPolledAt: 0, lastError: '',
    server: '', address: '', expectedUnits: '0',
  });

  const knownUtxos = useRef<Set<string>>(new Set());
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlightRef = useRef(false);
  const onDetectedRef = useRef(onDetected);
  onDetectedRef.current = onDetected;

  const expectedUnits = toBaseUnits(expectedDisplayAmount, coin);
  const categoryLower = coin.category.toLowerCase();

  // Keep the latest expected amount in a ref so the polling loop can read the
  // current value WITHOUT being torn down and restarted every time the USD
  // price refreshes (which was resetting polls to 1 and stalling detection).
  const expectedUnitsRef = useRef(expectedUnits);
  expectedUnitsRef.current = expectedUnits;

  const utxoKey = (u: FulcrumUtxo) => `${u.tx_hash}:${u.tx_pos}`;

  // Stable poll function — identity never changes, reads live values via refs.
  const poll = useCallback(async () => {
    const exp = expectedUnitsRef.current;
    if (!address || exp <= 0n) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    const minUnits = (exp * 80n) / 100n; // 80% tolerance

    try {
      const { utxos, server } = await fulcrumListUnspentTokens(address, 'tokens_only');
      setError(null);

      const matching = utxos.filter(
        (u) => u.token_data && u.token_data.category.toLowerCase() === categoryLower
      );

      console.debug(
        `[token-detection] ${coin.symbol} UTXOs=${matching.length}/${utxos.length} via ${server} (expect ≥ ${minUnits} units)`,
        matching.map((u) => ({ txid: u.tx_hash.slice(0, 10), amount: u.token_data?.amount })),
      );

      for (const u of matching) {
        const key = utxoKey(u);
        if (knownUtxos.current.has(key)) continue;
        const amount = BigInt(u.token_data!.amount);
        if (amount >= minUnits) {
          console.debug(`[token-detection] MATCH ${coin.symbol} ${u.tx_hash} amount=${amount}`);
          knownUtxos.current.add(key);
          setStatus('detected');
          onDetectedRef.current({ txid: u.tx_hash, amountUnits: amount.toString() });
          inFlightRef.current = false;
          return;
        }
      }

      setDebug((d) => ({
        ...d,
        polls: d.polls + 1,
        lastTokenUtxos: matching.length,
        lastPolledAt: Date.now(),
        lastError: '',
        server,
        address: address.replace(/^bitcoincash:/i, ''),
        expectedUnits: exp.toString(),
      }));
    } catch (e) {
      console.warn('[token-detection] error:', e);
      setError('Could not query token UTXOs');
      setDebug((d) => ({
        ...d,
        polls: d.polls + 1,
        lastPolledAt: Date.now(),
        lastError: e instanceof Error ? e.message : String(e),
        address: address.replace(/^bitcoincash:/i, ''),
        expectedUnits: exp.toString(),
      }));
    } finally {
      inFlightRef.current = false;
    }
  }, [address, coin.symbol, categoryLower]);

  // Set up polling ONCE per (active + address) session — NOT tied to the
  // expected amount, so price refreshes don't restart the loop.
  useEffect(() => {
    if (!active || !address) {
      setStatus('idle');
      return;
    }

    setStatus('waiting');
    setError(null);
    knownUtxos.current = new Set();
    inFlightRef.current = false;

    let cancelled = false;

    // Snapshot existing token UTXOs first so pre-existing ones don't false-trigger.
    fulcrumListUnspentTokens(address, 'tokens_only')
      .then(({ utxos }) => {
        if (cancelled) return;
        for (const u of utxos) {
          if (u.token_data && u.token_data.category.toLowerCase() === categoryLower) {
            knownUtxos.current.add(utxoKey(u));
          }
        }
      })
      .catch(() => { /* ignore snapshot errors */ })
      .finally(() => {
        if (cancelled) return;
        poll();
        intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
      });

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active, address, categoryLower, poll]);

  const reset = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setStatus('idle');
    setError(null);
    knownUtxos.current = new Set();
    inFlightRef.current = false;
  }, []);

  return { status, error, reset, debug };
}
